// src/components/pages/Auth/AuthLoginForm.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import {
  AuthErrorMessage,
  extractWpErrorMessage,
  mapWpErrorToUiMessage,
} from "../../GlobalFunction/AuthErrorMessage";

const isTWLocal = (p) => /^09\d{8}$/.test(String(p || "").replace(/\s|-/g, ""));

function isValidEmail(email) {
  const e = String(email || "").trim();
  // simple but reliable-enough check
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

/**
 * ✅ digits-only phone input
 * - strips ALL non-digits
 * - allows "9xxxxxxxx" and normalizes to "09xxxxxxxx"
 */
function normalizeTWPhoneInput(raw) {
  const digits = String(raw || "").replace(/\D/g, ""); // ✅ digits only
  if (/^9\d{8}$/.test(digits)) return "0" + digits;
  if (/^09\d{8}$/.test(digits)) return digits;
  return digits; // keep partial digits while typing
}

/* ------------------------------------------------------------
   ✅ Local translations for Forgot dialogs (ZH/EN/JP)
   - uses `lang` prop (already reacts to LangCookie changes upstream)
------------------------------------------------------------ */

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  if (s === "EN" || s === "JP" || s === "ZH") return s;
  return "ZH";
}

function pickForgotText(lang) {
  const L = normalizeLang(lang);

  const dict = {
    ZH: {
      descUsername:
        "請輸入您註冊時的電子郵件，我們將寄送您的帳號用戶名稱。",
      descPassword: "請輸入您註冊時的電子郵件，我們將寄送重設密碼連結。",
      emailLabel: "電子郵件",
      cancel: "取消",
      send: "送出",
      invalidEmail: "電子郵件格式不正確。",
      sentUsername: "已寄出帳號資訊（若此電子郵件已註冊）。",
      sentPassword: "已寄出重設密碼連結（若此電子郵件已註冊）。",
      sentPasswordWithMins: (mins) =>
        `已寄出重設密碼連結（若此電子郵件已註冊）。（約 ${mins} 分鐘有效）`,
      requestFailed: "送出失敗，請稍後再試。",
    },
    EN: {
      descUsername:
        "Enter your registered email. We’ll send your username to that email.",
      descPassword:
        "Enter your registered email. We’ll send you a password reset link.",
      emailLabel: "Email",
      cancel: "Cancel",
      send: "Send",
      invalidEmail: "Invalid email format.",
      sentUsername: "Sent (if the email is registered).",
      sentPassword: "Reset link sent (if the email is registered).",
      sentPasswordWithMins: (mins) =>
        `Reset link sent (if the email is registered). (Valid for ~${mins} min)`,
      requestFailed: "Request failed. Please try again.",
    },
    JP: {
      descUsername:
        "登録済みのメールアドレスを入力してください。ユーザー名を送信します。",
      descPassword:
        "登録済みのメールアドレスを入力してください。パスワード再設定リンクを送信します。",
      emailLabel: "メールアドレス",
      cancel: "キャンセル",
      send: "送信",
      invalidEmail: "メールアドレスの形式が正しくありません。",
      sentUsername: "送信しました（登録済みの場合）。",
      sentPassword: "再設定リンクを送信しました（登録済みの場合）。",
      sentPasswordWithMins: (mins) =>
        `再設定リンクを送信しました（登録済みの場合）。（有効期限：約 ${mins} 分）`,
      requestFailed: "送信に失敗しました。もう一度お試しください。",
    },
  };

  return dict[L] || dict.ZH;
}

export default function AuthLoginForm({
  rail,
  t,
  lang, // ✅ required for error localization
  loading,
  onSubmitLogin, // async ({ identifier, password, keepLogin })
  onGoRegister, // () => void
}) {
  const [loginMethod, setLoginMethod] = useState("phone"); // "phone" | "username"
  const [keepLogin, setKeepLogin] = useState(false);

  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");

  // -------------------- forgot dialogs --------------------
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotMode, setForgotMode] = useState("password"); // "username" | "password"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotErr, setForgotErr] = useState("");
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    msg: "",
  });

  const FORGOT = useMemo(() => pickForgotText(lang), [lang]);

  const identifierLabel =
    loginMethod === "phone" ? t("loginMethodPhone") : t("loginMethodUsername");

  const phoneNormalized = useMemo(() => normalizeTWPhoneInput(phone), [phone]);

  const phoneError =
    loginMethod === "phone" && phone && !isTWLocal(phoneNormalized)
      ? t("phoneFormatShort")
      : "";

  const switchMethodText =
    loginMethod === "phone"
      ? t("loginSwitchToUsername")
      : t("loginSwitchToPhone");

  const requiredHandlers = {
    onInvalid: (e) => {
      e.target.setCustomValidity(t("requiredField"));
    },
    onInput: (e) => {
      e.target.setCustomValidity("");
    },
  };

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;

    setError("");

    let identifier = "";
    if (loginMethod === "phone") {
      const local = normalizeTWPhoneInput(phone);
      if (!isTWLocal(local)) {
        setError(t("loginPhoneFormatError"));
        return;
      }
      identifier = local;
    } else {
      const u = String(username || "").trim();
      if (!u) {
        setError(t("loginUsernameRequired"));
        return;
      }
      identifier = u;
    }

    if (!password) {
      setError(t("requiredField"));
      return;
    }

    try {
      await onSubmitLogin({
        identifier,
        password,
        keepLogin: !!keepLogin,
      });

      // clear after success
      setPassword("");
      setUsername("");
      setPhone("");
      setError("");
    } catch (err) {
      // ✅ decode/clean WP HTML error and map to user-friendly localized message
      const raw = extractWpErrorMessage(err);
      const uiMsg = mapWpErrorToUiMessage({
        rawMsg: raw,
        lang,
        t,
        fallbackKey: "loginFailed",
      });
      setError(uiMsg || t("loginFailed"));
    }
  }

  function openForgot(mode) {
    if (loading) return;

    setForgotMode(mode);
    setForgotErr("");
    setForgotBusy(false);

    // optional: if user typed something that looks like email, prefill it
    const guess =
      (loginMethod === "username" &&
        isValidEmail(username) &&
        String(username).trim()) ||
      "";

    setForgotEmail(guess);
    setForgotOpen(true);
  }

  function closeForgot() {
    if (forgotBusy) return;
    setForgotOpen(false);
    setForgotErr("");
    setForgotEmail("");
  }

  async function submitForgot() {
    if (forgotBusy) return;

    const email = String(forgotEmail || "").trim();
    if (!email) {
      setForgotErr(t("requiredField"));
      return;
    }
    if (!isValidEmail(email)) {
      // ✅ localized dialog error message
      setForgotErr(FORGOT.invalidEmail);
      return;
    }

    setForgotErr("");
    setForgotBusy(true);

    try {
      const { wpForgotUsername, wpForgotPassword } =
        await import("../../../api/wpAuth");

      const langToUse = String(lang || "ZH")
        .toUpperCase()
        .trim();

      if (forgotMode === "username") {
        const res = await wpForgotUsername({ email, lang: langToUse });

        // backend may return ok=false with message; still show it cleanly
        const ok = !!res?.ok;
        const backendMsg = String(res?.message || "").trim();

        setToast({
          open: true,
          severity: ok ? "success" : "warning",
          msg: ok ? FORGOT.sentUsername : backendMsg || FORGOT.requestFailed,
        });
      } else {
        const res = await wpForgotPassword({ email, lang: langToUse });

        const ok = !!res?.ok;
        const backendMsg = String(res?.message || "").trim();

        const expiresIn = Number(res?.expiresIn || 0);
        const mins =
          expiresIn > 0 ? Math.max(1, Math.floor(expiresIn / 60)) : 0;

        setToast({
          open: true,
          severity: ok ? "success" : "warning",
          msg: ok
            ? mins
              ? FORGOT.sentPasswordWithMins(mins)
              : FORGOT.sentPassword
            : backendMsg || FORGOT.requestFailed,
        });
      }

      setForgotOpen(false);
      setForgotBusy(false);
      setForgotEmail("");
    } catch (err) {
      const raw = extractWpErrorMessage(err);
      const uiMsg = mapWpErrorToUiMessage({
        rawMsg: raw,
        lang,
        t,
        fallbackKey: "requestFailed",
      });

      setForgotErr(uiMsg || raw || FORGOT.requestFailed);
      setForgotBusy(false);
    }
  }

  const forgotTitle =
    forgotMode === "username" ? t("forgotUsername") : t("forgotPassword");

  const forgotDesc =
    forgotMode === "username" ? FORGOT.descUsername : FORGOT.descPassword;

  return (
    <>
      <Stack spacing={1.5} component="form" onSubmit={handleSubmit}>
        <Typography
          variant="h5"
          sx={{ mb: 0.5, textAlign: "center", fontWeight: 600 }}
        >
          {t("loginTitle")}
        </Typography>

        {/* Identifier input */}
        {loginMethod === "phone" ? (
          <Box>
            <TextField
              size="small"
              label={identifierLabel}
              value={phone}
              onChange={(e) => setPhone(normalizeTWPhoneInput(e.target.value))}
              fullWidth
              required
              autoComplete="tel"
              type="tel"
              error={!!phoneError}
              helperText={phoneError || " "}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 10,
              }}
              {...requiredHandlers}
            />

            {/* ✅ spacing + button under input */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0 }}>
              <Button
                type="button"
                variant="text"
                disabled={loading}
                onClick={() => {
                  setError("");
                  setLoginMethod("username");
                }}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0,
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                {switchMethodText}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <TextField
              size="small"
              label={identifierLabel}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              required
              autoComplete="username"
              helperText=" "
              {...requiredHandlers}
            />

            {/* ✅ spacing + button under input */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0 }}>
              <Button
                type="button"
                variant="text"
                disabled={loading}
                onClick={() => {
                  setError("");
                  setLoginMethod("phone");
                }}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0,
                  fontSize: 12,
                  textTransform: "none",
                }}
              >
                {switchMethodText}
              </Button>
            </Box>
          </Box>
        )}

        <TextField
          size="small"
          type={showPwd ? "text" : "password"}
          label={t("passwordLabel")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          required
          autoComplete="current-password"
          inputProps={{
            spellCheck: "false",
            autoCapitalize: "none",
            autoCorrect: "off",
          }}
          {...requiredHandlers}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPwd ? t("hidePassword") : t("showPassword")}
                  edge="end"
                  size="small"
                  onClick={() => setShowPwd((v) => !v)}
                >
                  {showPwd ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* ✅ Forgot buttons */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: -0.25 }}
        >
          <Button
            type="button"
            variant="text"
            disabled={loading}
            onClick={() => openForgot("username")}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            {t("forgotUsername")}
          </Button>

          <Button
            type="button"
            variant="text"
            disabled={loading}
            onClick={() => openForgot("password")}
            sx={{ textTransform: "none", fontSize: 12 }}
          >
            {t("forgotPassword")}
          </Button>
        </Stack>

        <FormControlLabel
          control={
            <Checkbox
              checked={keepLogin}
              onChange={(e) => setKeepLogin(e.target.checked)}
            />
          }
          label={t("keepLogin")}
          sx={{ mt: 0.25 }}
        />

        <AuthErrorMessage text={error} sx={{ mt: 0.25 }} />

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          size="large"
          sx={{ mt: 0.5, bgcolor: rail }}
        >
          {t("loginButton")}
        </Button>

        {/* Go register */}
        <Stack direction="row" justifyContent="center" sx={{ mt: 0.5 }}>
          <Button
            type="button"
            variant="text"
            disabled={loading}
            onClick={onGoRegister}
            sx={{ textTransform: "none" }}
          >
            {t("tabSignup")}
          </Button>
        </Stack>
      </Stack>

      {/* ✅ Forgot dialog */}
      <Dialog open={forgotOpen} onClose={closeForgot} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{forgotTitle}</DialogTitle>

        <DialogContent>
          <Stack spacing={1.25} sx={{ pt: 0.5 }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {forgotDesc}
            </Typography>

            <TextField
              size="small"
              label={FORGOT.emailLabel}
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              autoComplete="email"
              type="email"
              fullWidth
              disabled={forgotBusy}
              error={!!forgotErr}
              helperText={forgotErr || " "}
            />

            {forgotBusy && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 0.5 }}>
                <CircularProgress size={22} />
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeForgot} disabled={forgotBusy}>
            {FORGOT.cancel}
          </Button>
          <Button
            onClick={submitForgot}
            variant="contained"
            disabled={forgotBusy}
          >
            {FORGOT.send}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Toast */}
      <Snackbar
        open={!!toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((v) => ({ ...v, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity || "success"}
          onClose={() => setToast((v) => ({ ...v, open: false }))}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
